import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Storage keys
const TOKEN_KEY = 'token';
const IMP_STACK_KEY = 'imp_stack'; // JSON array of prior tokens we can pop back to

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const fetchMe = async (t) => {
    const r = await axios.get(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${t}` }
    });
    return r.data;
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      if (storedToken) {
        try {
          const me = await fetchMe(storedToken);
          setUser(me);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(IMP_STACK_KEY);
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.removeItem(IMP_STACK_KEY); // fresh login clears any prior impersonation
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(IMP_STACK_KEY);
    setToken(null);
    setUser(null);
  };

  /** Swap the current session for an impersonation session. Saves the prior
   *  token on the stack so `stopImpersonating()` restores it. */
  const beginImpersonation = async (userId) => {
    const current = localStorage.getItem(TOKEN_KEY);
    if (!current) throw new Error('Not authenticated');
    const r = await axios.post(
      `${API_URL}/api/admin/impersonate/${userId}`,
      {},
      { headers: { Authorization: `Bearer ${current}` } }
    );
    const { access_token, target_user } = r.data;
    // Push current token onto stack
    const stack = JSON.parse(localStorage.getItem(IMP_STACK_KEY) || '[]');
    stack.push(current);
    localStorage.setItem(IMP_STACK_KEY, JSON.stringify(stack));
    localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    // Refresh user from /me so `impersonated_by` fields are populated
    const me = await fetchMe(access_token);
    setUser(me);
    return target_user;
  };

  const stopImpersonating = async () => {
    const stack = JSON.parse(localStorage.getItem(IMP_STACK_KEY) || '[]');
    const prior = stack.pop();
    if (!prior) {
      // Nothing to restore — fall back to full logout
      logout();
      return;
    }
    localStorage.setItem(IMP_STACK_KEY, JSON.stringify(stack));
    localStorage.setItem(TOKEN_KEY, prior);
    setToken(prior);
    const me = await fetchMe(prior);
    setUser(me);
  };

  const isImpersonating = !!user?.impersonated_by;
  const mustChangePassword = !!user?.must_change_password && !isImpersonating;

  const refreshMe = async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return null;
    const me = await fetchMe(t);
    setUser(me);
    return me;
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout, getAuthHeader,
      beginImpersonation, stopImpersonating, isImpersonating,
      mustChangePassword, refreshMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
